import { useState, type ChangeEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BackButton } from '@/components/ui/back-button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { organizationApi } from '@/lib/pillarApi';
import {
    MerchantBackupV1Schema,
    type MerchantBackupV1,
    type RestoreMerchantBackupDryRunResponse,
} from '@pillar-portal/api-types';

export default function MerchantBackupPage() {
    const [selectedBackup, setSelectedBackup] = useState<MerchantBackupV1 | null>(null);
    const [selectedFilename, setSelectedFilename] = useState<string | null>(null);
    const [parseErrorMessage, setParseErrorMessage] = useState<string | null>(null);
    const [showApplyConfirmation, setShowApplyConfirmation] = useState<boolean>(false);

    const dryRunMutation = useMutation({
        mutationFn: async (backup: MerchantBackupV1): Promise<RestoreMerchantBackupDryRunResponse> =>
            await organizationApi.restoreMerchantBackupDryRun({ backup }),
    });

    const applyMutation = useMutation({
        mutationFn: async (backup: MerchantBackupV1) =>
            await organizationApi.restoreMerchantBackupApply({ backup }),
    });

    const onFileSelected = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
        const selectedFile: File | undefined = event.target.files?.[0];
        if (selectedFile === undefined) {
            return;
        }
        setParseErrorMessage(null);
        setSelectedBackup(null);
        setSelectedFilename(selectedFile.name);
        dryRunMutation.reset();
        applyMutation.reset();
        try {
            const fileContents: string = await selectedFile.text();
            const parsedJson: unknown = JSON.parse(fileContents);
            const parsedBackup: MerchantBackupV1 = MerchantBackupV1Schema.parse(parsedJson);
            setSelectedBackup(parsedBackup);
        } catch (error: unknown) {
            const errorMessage: string =
                error instanceof Error ? error.message : 'Failed to parse backup file.';
            setParseErrorMessage(errorMessage);
        }
    };

    const runDryRun = (): void => {
        if (selectedBackup === null) {
            return;
        }
        dryRunMutation.mutate(selectedBackup);
    };

    const confirmApplyRestore = (): void => {
        if (selectedBackup === null) {
            return;
        }
        applyMutation.mutate(selectedBackup);
        setShowApplyConfirmation(false);
    };

    return (
        <div className="space-y-6">
            <BackButton href="/dashboard" tooltip="Back to Dashboard" />
            <h1 className="text-3xl font-bold">Load Merchant Backup</h1>

            <Card>
                <CardHeader>
                    <CardTitle>Select Backup File</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <input type="file" accept="application/json" onChange={onFileSelected} />
                    {selectedFilename !== null ? (
                        <div className="text-sm text-muted-foreground">
                            Selected file: {selectedFilename}
                        </div>
                    ) : null}
                    {parseErrorMessage !== null ? (
                        <div className="text-sm text-destructive">{parseErrorMessage}</div>
                    ) : null}
                    <Button
                        onClick={runDryRun}
                        disabled={selectedBackup === null || dryRunMutation.isPending}
                    >
                        {dryRunMutation.isPending ? 'Running dry run...' : 'Dry run'}
                    </Button>
                </CardContent>
            </Card>

            {dryRunMutation.data !== undefined ? (
                <Card>
                    <CardHeader>
                        <CardTitle>Dry Run Result</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="text-sm">
                            Valid: {dryRunMutation.data.valid ? 'Yes' : 'No'}
                        </div>
                        <div className="text-sm">
                            Organizations: {dryRunMutation.data.summary.organizations}
                        </div>
                        <div className="text-sm">Users: {dryRunMutation.data.summary.users}</div>
                        <div className="text-sm">
                            Permission bundles: {dryRunMutation.data.summary.permissions_bundles}
                        </div>
                        <div className="text-sm">
                            Role assignments: {dryRunMutation.data.summary.role_assignments}
                        </div>
                        <div className="text-sm">
                            Policy grants: {dryRunMutation.data.summary.permissions_policy_grants}
                        </div>
                        <div className="text-sm">
                            Credentials: {dryRunMutation.data.summary.credentials}
                        </div>
                        <div className="text-sm">
                            Merchant accounts: {dryRunMutation.data.summary.merchant_accounts}
                        </div>
                        <div className="text-sm">
                            Logical terminals: {dryRunMutation.data.summary.logical_terminals}
                        </div>
                        <div className="text-sm">
                            Terminal devices: {dryRunMutation.data.summary.terminal_devices}
                        </div>
                        {dryRunMutation.data.warnings.length > 0 ? (
                            <div className="space-y-1">
                                <div className="text-sm font-semibold">Warnings</div>
                                {dryRunMutation.data.warnings.map((warning: string, index: number) => (
                                    <div key={`${warning}-${index}`} className="text-sm text-destructive">
                                        - {warning}
                                    </div>
                                ))}
                            </div>
                        ) : null}
                        <Button
                            variant="destructive"
                            disabled={!dryRunMutation.data.valid || applyMutation.isPending}
                            onClick={() => {
                                setShowApplyConfirmation(true);
                            }}
                        >
                            {applyMutation.isPending ? 'Restoring...' : 'Apply restore'}
                        </Button>
                        {applyMutation.error !== null ? (
                            <div className="text-sm text-destructive">
                                {String(applyMutation.error)}
                            </div>
                        ) : null}
                        {applyMutation.data !== undefined ? (
                            <div className="text-sm">
                                Restore completed. New organization:{' '}
                                <Link
                                    className="text-primary underline"
                                    to={`/organizations/${applyMutation.data.target_organization_uuid}?view=merchant`}
                                >
                                    {applyMutation.data.target_organization_name}
                                </Link>
                            </div>
                        ) : null}
                    </CardContent>
                </Card>
            ) : null}

            <ConfirmationModal
                open={showApplyConfirmation}
                onOpenChange={setShowApplyConfirmation}
                message="Apply restore and create a new organization from this backup?"
                confirmLabel="Restore"
                onConfirm={confirmApplyRestore}
                isLoading={applyMutation.isPending}
            />
        </div>
    );
}
